import type {
  FormattedChannel,
  FormattedNotification
} from '@library/types/alerts';
import {
  useAcknowledgeNotificationMutation,
  useCreateNotificationMutation,
  useGetAlertNotificationsQuery
} from '@bo-services/alertsApi';
import { useCallback } from 'react';

export const useAlertNotifications = () => {
  const { data: alertNotificationData, isFetching } =
    useGetAlertNotificationsQuery();

  const [acknowledgeNotificationMutation] =
    useAcknowledgeNotificationMutation();

  const [createNotificationMutation, { isError, isSuccess }] =
    useCreateNotificationMutation();

  const handleReadNotification = useCallback(
    async (
      notification: FormattedNotification,
      setSelectedChannel: (channel: FormattedChannel) => void,
      setSpecificAlertId: (alertId: string) => void,
      setAcknowledged: (read: boolean) => void
    ) => {
      if (setSelectedChannel && setSpecificAlertId) {
        try {
          setSelectedChannel({
            description: '',
            displayName: notification.channelName,
            id: Number(notification.channelId),
            isUserSubscribed: true,
            isChannelUnread: false
          });
          setSpecificAlertId(notification.messageId);
          await acknowledgeNotificationMutation({ id: notification.id });
          setAcknowledged(true);
        } catch (error: unknown) {
          // TODO: alerts handle error more gracefully
          // eslint-disable-next-line no-console
          console.log(`Error: ${String(error)}`);
        }
      }
    },
    [acknowledgeNotificationMutation]
  );

  const handleTagUser = useCallback(
    async (
      taggedUserId: string,
      alertId: string,
      selectedChannelId: number,
      setIsFetching: (isFetching: boolean) => void,
      onCancelModal: () => void
    ) => {
      if (setIsFetching) {
        try {
          setIsFetching(true);
          await createNotificationMutation({
            channelId: selectedChannelId.toString(),
            messageId: alertId,
            destination_user_id: taggedUserId
          });
          onCancelModal();
        } catch (error: unknown) {
          setIsFetching(false);
          onCancelModal();
        }
        setIsFetching(false);
      }
    },
    [createNotificationMutation]
  );

  return {
    alertNotificationData,
    isFetching,
    handleReadNotification,
    handleTagUser,
    tagUserError: isError,
    tagUserSuccess: isSuccess
  };
};
